/**
 * SEO Monitor — per-site SEO + Ads config helpers.
 *
 * Defines the canonical shape of websites.seo_config and the ads sub-tree
 * of websites.monetization_config, plus pure helpers (validator URLs,
 * sitemap URL, defaults) used by the SEO Monitor page and the public
 * template renderer's AdSlot component.
 */

export type AdSlotKey =
  | "header"
  | "between_articles"
  | "sidebar"
  | "in_article"
  | "sticky_footer";

export const AD_SLOTS: Array<{
  key: AdSlotKey;
  labelKey: string;
  helpKey: string;
  position: string;
}> = [
  { key: "header", labelKey: "seoMonitor.ads.slot.header.label", helpKey: "seoMonitor.ads.slot.header.help", position: "above the masthead" },
  { key: "between_articles", labelKey: "seoMonitor.ads.slot.between.label", helpKey: "seoMonitor.ads.slot.between.help", position: "every 3 cards" },
  { key: "sidebar", labelKey: "seoMonitor.ads.slot.sidebar.label", helpKey: "seoMonitor.ads.slot.sidebar.help", position: "right column" },
  { key: "in_article", labelKey: "seoMonitor.ads.slot.inArticle.label", helpKey: "seoMonitor.ads.slot.inArticle.help", position: "after paragraph 3" },
  { key: "sticky_footer", labelKey: "seoMonitor.ads.slot.stickyFooter.label", helpKey: "seoMonitor.ads.slot.stickyFooter.help", position: "fixed bottom" },
];

export interface SeoConfig {
  og_image_url: string | null;
  twitter_handle: string | null;
  google_site_verification: string | null;
  bing_site_verification: string | null;
  yandex_site_verification: string | null;
  pinterest_verification: string | null;
  sitemap_url: string | null;
  sitemap_submitted_at: string | null;
  search_console_property: string | null;
  robots_txt_extra: string | null;
}

export function defaultSeoConfig(): SeoConfig {
  return {
    og_image_url: null,
    twitter_handle: null,
    google_site_verification: null,
    bing_site_verification: null,
    yandex_site_verification: null,
    pinterest_verification: null,
    sitemap_url: null,
    sitemap_submitted_at: null,
    search_console_property: null,
    robots_txt_extra: null,
  };
}

export interface AdSenseConfig {
  publisher_id: string | null;
  auto_ads: boolean;
  ads_txt: string | null;
  slots: Partial<Record<AdSlotKey, boolean>>;
}

export interface AdsConfig {
  google_adsense?: AdSenseConfig;
  google_ad_manager?: { network_code: string | null };
}

export function defaultAdsConfig(): AdsConfig {
  return {
    google_adsense: {
      publisher_id: null,
      auto_ads: false,
      ads_txt: null,
      slots: {},
    },
  };
}

export function buildSitemapUrl(domain: string | null, slug: string): string {
  const host = domain ? domain.replace(/^https?:\/\//, "").replace(/\/$/, "") : `${slug}.delapresse.com`;
  return `https://${host}/sitemap.xml`;
}

export function buildSocialValidatorLinks(siteUrl: string) {
  const u = encodeURIComponent(siteUrl);
  return {
    facebook: `https://developers.facebook.com/tools/debug/?q=${u}`,
    twitter: `https://cards-dev.twitter.com/validator`,
    linkedin: `https://www.linkedin.com/post-inspector/inspect/${u}`,
    openGraph: `https://www.opengraph.xyz/url/${u}`,
  };
}

export function buildSearchSubmissionLinks(sitemapUrl: string, siteUrl: string) {
  const sm = encodeURIComponent(sitemapUrl);
  const u = encodeURIComponent(siteUrl);
  return {
    googleSearchConsole: `https://search.google.com/search-console/sitemaps?resource_id=${u}`,
    bingWebmaster: `https://www.bing.com/webmasters/sitemaps?siteUrl=${u}`,
    googleSitemapPing: `https://www.google.com/ping?sitemap=${sm}`,
    indexNow: `https://www.indexnow.org/`,
  };
}
